import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import useTranslate from "../../hooks/useTranslate";
import { loungeService } from "../../services/loungeService";
import { formatFileSize } from "../inbox/sharedContent";

// Post attachments (QAL-T7) — design source Agent Lounge.dc.html:
//   * PDF row: frame 1a Z.166-170, token-exact (38px tile #EDF4FF, name
//     13.5/600 ellipsised, size 12.5 #9CA3AF, download glyph right). The
//     drawn meta says "640 KB · 4 pages" — the "N pages" part is a DECLARED
//     deviation (no PDF parsing server-side, contract §4): size only.
//   * Image area: the drawn single-image style (full width, 200px, 12px
//     radius, 1px border #DDE3EB) carries the Twitter grid from GATE0 §5 —
//     1 = full, 2 = halves, 3 = one large left + two stacked right, 1px
//     seams, object-fit cover.
//   * Click on an image opens a plain overlay (ground rgba(17,24,39,0.40)
//     from the drawn dialog scrims; click/Escape closes) — DESIGN GAP FILL,
//     no lightbox is drawn anywhere, kept minimal.
//
// Bytes NEVER load from a raw S3 URL — the payload carries none (§2). Every
// image/PDF goes through loungeService.fetchAttachmentBlob (Bearer,
// permission-checked endpoint, 302 presigned) and is drawn from an object
// URL; the ref-counted cache revokes it after the last unmount.

// One blob-backed object URL per attachment id — paired acquire/release.
export const useAttachmentUrl = (id) => {
  const [state, setState] = useState({ url: null, failed: false });
  useEffect(() => {
    if (id == null) return undefined;
    let alive = true;
    setState({ url: null, failed: false });
    loungeService
      .fetchAttachmentBlob(id)
      .then((url) => {
        if (alive) setState({ url, failed: false });
      })
      .catch(() => {
        if (alive) setState({ url: null, failed: true });
      });
    return () => {
      alive = false;
      loungeService.releaseAttachmentBlob(id);
    };
  }, [id]);
  return state;
};

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="shrink-0">
    <path
      d="M8 2v8m0 0L4.8 6.8M8 10l3.2-3.2M3 13h10"
      stroke="#6B7280"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Plain image overlay — design gap fill (see header). Click anywhere or
// Escape closes; the image itself is the already-fetched blob URL.
export const ImageOverlay = ({ url, alt, onClose }) => {
  const { t } = useTranslate();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-[rgba(17,24,39,0.40)] flex items-center justify-center p-4 sm:p-8 cursor-zoom-out"
      onClick={onClose}
      role="button"
      tabIndex={-1}
      aria-label={t("Close image")}
      data-testid="image-overlay"
    >
      <img
        src={url}
        alt={alt || ""}
        className="max-w-full max-h-full rounded-[12px] shadow-[0_16px_40px_rgba(17,24,39,0.30)]"
      />
    </div>
  );
};

// One grid cell — shimmer until the blob lands, quiet ground on failure.
const GridImage = ({ attachment, onOpen, className = "" }) => {
  const { tv } = useTranslate();
  const { url, failed } = useAttachmentUrl(attachment.id);
  return (
    <button
      type="button"
      onClick={() => url && onOpen(url, attachment.file_name)}
      aria-label={tv("View image {{name}}", { name: attachment.file_name || "" })}
      className={`relative block w-full h-full min-w-0 min-h-0 p-0 border-0 bg-transparent overflow-hidden ${
        url ? "cursor-zoom-in" : "cursor-default"
      } ${className}`}
      data-testid="grid-image"
    >
      {url ? (
        <img src={url} alt={attachment.file_name || ""} className="block w-full h-full object-cover" />
      ) : (
        <span className={`block w-full h-full ${failed ? "bg-[#F1F5FA]" : "tp-shimmer"}`} />
      )}
    </button>
  );
};

// Twitter grid inside the drawn single-image frame (GATE0 §5): the seam
// color IS the border color — the container ground shows through the 1px
// gaps.
export const LoungeImageGrid = ({ images, className = "" }) => {
  const [overlay, setOverlay] = useState(null); // {url, alt}
  const n = Math.min(images.length, 3);
  const layout =
    n === 1
      ? "lounge-grid-1 grid-cols-1"
      : n === 2
        ? "lounge-grid-2 grid-cols-2"
        : "lounge-grid-3 grid-cols-2 grid-rows-2";
  return (
    <>
      <div
        className={`grid gap-px w-full h-[200px] rounded-[12px] border border-[#DDE3EB] bg-[#DDE3EB] overflow-hidden ${layout} ${className}`}
        data-testid="lounge-image-grid"
      >
        {images.slice(0, 3).map((att, i) => (
          <GridImage
            key={att.id}
            attachment={att}
            onOpen={(url, alt) => setOverlay({ url, alt })}
            className={n === 3 && i === 0 ? "row-span-2" : ""}
          />
        ))}
      </div>
      {overlay && (
        <ImageOverlay url={overlay.url} alt={overlay.alt} onClose={() => setOverlay(null)} />
      )}
    </>
  );
};

// PDF row — 1a Z.166-170 token-exact; click downloads the blob under its
// original file name (no inline viewer is drawn).
export const LoungePdfRow = ({ attachment, className = "" }) => {
  const { t, tv } = useTranslate();
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const url = await loungeService.fetchAttachmentBlob(attachment.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.file_name || "document.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(t("Something went wrong. Please try again."));
    } finally {
      // Give the download time to start — releasing at zero refs revokes
      // the object URL, which would cut a just-started download off.
      setTimeout(() => loungeService.releaseAttachmentBlob(attachment.id), 10000);
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={download}
      aria-label={tv("Download {{name}}", { name: attachment.file_name || "PDF" })}
      className={`w-full flex items-center gap-3 border border-[#DDE3EB] rounded-[12px] p-[11px_14px] bg-transparent text-left cursor-pointer hover:border-[#C6CFDA] hover:bg-[#F8FAFC] ${
        downloading ? "opacity-70" : ""
      } ${className}`}
      data-testid="pdf-row"
    >
      <span className="w-[38px] h-[38px] rounded-[9px] bg-[#EDF4FF] text-[#2283FF] flex items-center justify-center text-[10.5px] font-bold shrink-0">
        PDF
      </span>
      <span className="flex flex-col min-w-0 flex-1">
        <span className="text-[13.5px] font-semibold text-[#111827] whitespace-nowrap overflow-hidden text-ellipsis">
          {attachment.file_name}
        </span>
        <span className="text-[12.5px] text-[#9CA3AF]">
          {formatFileSize(attachment.size_bytes)}
        </span>
      </span>
      <DownloadIcon />
    </button>
  );
};

// Card/detail entry point: sorts by `order`, draws the grid OR the PDF row
// (contract: never mixed). `className` carries the card's bottom rhythm.
const LoungeAttachments = ({ attachments, className = "" }) => {
  const list = Array.isArray(attachments)
    ? attachments.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];
  if (list.length === 0) return null;
  const images = list.filter((a) => a.kind === "image");
  const pdf = list.find((a) => a.kind === "pdf");
  if (images.length > 0) return <LoungeImageGrid images={images} className={className} />;
  if (pdf) return <LoungePdfRow attachment={pdf} className={className} />;
  return null;
};

export default LoungeAttachments;
